import * as Sentry from "@sentry/browser";
//#region ../src/utils/logger.ts
/**
* Configurable Logger Utility
*
* Provides a simple logging API with configurable log levels.
* This allows disabling verbose logging in production while keeping
* it available for development and debugging.
*
* Also maintains an in-memory ring buffer of recent log entries
* for display in the expandable log panel UI.
*
* Sentry Integration:
* - All log levels add Sentry breadcrumbs for debugging context
* - log.error() with Error objects automatically reports to Sentry
*/
/**
* Log levels in order of verbosity (lower = more verbose)
*/
let LogLevel = /* @__PURE__ */ function(LogLevel) {
	LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
	LogLevel[LogLevel["INFO"] = 1] = "INFO";
	LogLevel[LogLevel["WARN"] = 2] = "WARN";
	LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
	LogLevel[LogLevel["SILENT"] = 4] = "SILENT";
	return LogLevel;
}({});
/**
* Global log level - controls what gets logged across all loggers
*/
let globalLogLevel = 0;
/**
* Maximum number of log entries to keep in the ring buffer
*/
const LOG_BUFFER_MAX_SIZE = 100;
/**
* In-memory ring buffer of recent log entries
*/
let logBuffer = [];
let subscribers = [];
/**
* Serialize an Error instance to an object capturing name, message, stack,
* and any enumerable properties.
*/
function serializeError(error) {
	const result = {
		name: error.name,
		message: error.message,
		stack: error.stack
	};
	for (const key of Object.keys(error)) result[key] = error[key];
	return result;
}
/**
* Safely stringify an object, handling circular references.
* Uses a WeakSet to track seen objects and replaces cycles with a placeholder.
*/
function safeStringify(value) {
	const seen = /* @__PURE__ */ new WeakSet();
	const replacer = (_key, val) => {
		if (typeof val === "bigint") return val.toString();
		if (val !== null && typeof val === "object") {
			if (seen.has(val)) return "[Circular]";
			seen.add(val);
		}
		return val;
	};
	try {
		return JSON.stringify(value, replacer);
	} catch {
		return "[Unserializable]";
	}
}
/**
* Serialize a single argument to a string for the log buffer.
* Special-cases Error instances and handles circular/complex objects safely.
*/
function serializeArg(arg) {
	if (arg === null) return "null";
	if (arg === void 0) return "undefined";
	if (typeof arg === "string") return arg;
	if (typeof arg === "number" || typeof arg === "boolean") return String(arg);
	if (typeof arg === "bigint") return arg.toString();
	if (typeof arg === "symbol") return arg.toString();
	if (typeof arg === "function") return arg.name ? `[Function: ${arg.name}]` : "[Function]";
	if (arg instanceof Error) return safeStringify(serializeError(arg));
	return safeStringify(arg);
}
/**
* Add an entry to the log buffer and notify subscribers
*/
function addToBuffer(level, tag, args) {
	const message = args.map((arg) => serializeArg(arg)).join(" ");
	const entry = {
		timestamp: Date.now(),
		level,
		tag,
		message
	};
	logBuffer.push(entry);
	if (logBuffer.length > LOG_BUFFER_MAX_SIZE) logBuffer.shift();
	for (const subscriber of subscribers) try {
		subscriber(entry);
	} catch (err) {
		console.error("[Logger] Subscriber threw an error:", err);
	}
}
/**
* Get a copy of the current log buffer
* @returns Array of log entries (copy to prevent external mutation)
*/
function getLogBuffer() {
	return [...logBuffer];
}
/**
* Clear all entries from the log buffer
*/
function clearLogBuffer() {
	logBuffer = [];
}
/**
* Subscribe to new log entries
* @param callback - Function called when a new log entry is added
* @returns Unsubscribe function
*/
function subscribeToLogs(callback) {
	subscribers.push(callback);
	return () => {
		subscribers = subscribers.filter((sub) => sub !== callback);
	};
}
/**
* Set the global log level
* @param level - The minimum level to log
*/
function setGlobalLogLevel(level) {
	globalLogLevel = level;
}
/**
* Get the current global log level
*/
function getGlobalLogLevel() {
	return globalLogLevel;
}
/**
* Create a logger with a specific tag prefix
* @param tag - The tag to prefix all log messages with (e.g., 'GPS', 'Storage')
* @returns A logger object with debug, info, warn, error methods
*
* @example
* ```typescript
* const log = createLogger('GPS');
* log.info('Watch started'); // [GPS] Watch started
* log.error('Error:', err);  // [GPS] Error: <error details>
* ```
*/
/**
* Map LogLevel to Sentry breadcrumb severity.
* Sentry uses 'warning' (not 'warn') for the warning level.
*/
function toSentryLevel(level) {
	switch (level) {
		case 0: return "debug";
		case 1: return "info";
		case 2: return "warning";
		case 3: return "error";
		default: return "info";
	}
}
/**
* Add a Sentry breadcrumb for debugging context.
* Breadcrumbs are attached to future exceptions, helping trace what happened before an error.
*/
function addSentryBreadcrumb(level, tag, args) {
	const message = `[${tag}] ${args.map((arg) => serializeArg(arg)).join(" ")}`;
	Sentry.addBreadcrumb({
		category: "log",
		level: toSentryLevel(level),
		message
	});
}
/**
* Report a warning to Sentry as a standalone message.
* Called for log.warn() so warnings are independently visible in the
* Sentry dashboard, not only as breadcrumbs attached to later exceptions.
*/
function reportWarningToSentry(tag, args) {
	const message = `[${tag}] ${args.map((arg) => serializeArg(arg)).join(" ")}`;
	Sentry.captureMessage(message, "warning");
}
/**
* Report Error objects to Sentry.
* Only called for log.error() - other levels just add breadcrumbs.
*/
function reportErrorsToSentry(args) {
	for (const arg of args) if (arg instanceof Error) Sentry.captureException(arg);
}
function createLogger(tag) {
	const prefix = `[${tag}]`;
	return {
		debug: (...args) => {
			addToBuffer(0, tag, args);
			addSentryBreadcrumb(0, tag, args);
			if (globalLogLevel <= 0) console.log(prefix, ...args);
		},
		info: (...args) => {
			addToBuffer(1, tag, args);
			addSentryBreadcrumb(1, tag, args);
			if (globalLogLevel <= 1) console.log(prefix, ...args);
		},
		warn: (...args) => {
			addToBuffer(2, tag, args);
			addSentryBreadcrumb(2, tag, args);
			reportWarningToSentry(tag, args);
			if (globalLogLevel <= 2) console.warn(prefix, ...args);
		},
		error: (...args) => {
			addToBuffer(3, tag, args);
			addSentryBreadcrumb(3, tag, args);
			reportErrorsToSentry(args);
			if (globalLogLevel <= 3) console.error(prefix, ...args);
		}
	};
}
//#endregion
export { LogLevel, clearLogBuffer, createLogger, getGlobalLogLevel, getLogBuffer, setGlobalLogLevel, subscribeToLogs };
